// eslint-disable-next-line import/no-extraneous-dependencies
import { FastAverageColor } from 'fast-average-color';
import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { sortByOrder } from 'src/helpers';
import { AmenitiesFirestoreResponse } from 'src/types';
import { IMAGE_PLACEHOLDER } from 'src/utils/variables';
import { Swiper, SwiperSlide } from 'swiper/react';

import STAR from '../../assets/images/star-icon.png';
import { Banner, Card, Flex, LightboxGallery, Modal, Spinner } from '../../components';
import { useGetHotelAmenitiesFromFirestore, useGetHotelDocumentFromFirestore, useQueryHelper } from '../../queries';
import { useLanguageState } from '../../state';
import {
  Addres,
  ContentContainer,
  CoverImageWrapper,
  HotelLogoContainer,
  HotelLogoWrapper,
  HotelName,
  Image,
  LastSection,
  Logo,
  Section,
  SectionTitle,
  Text,
  TextContainer,
} from './AboutHotel.styled';
import { CardDirector, Quote } from './components';

interface AboutHotelProps {
  id: string;
}

export const AboutHotel: FC<AboutHotelProps> = ({ id }) => {
  const { t } = useTranslation();
  const { language: currentLang } = useLanguageState();
  const [showModal, setShowModal] = useState<string | null>(null);
  const [showAmenitiesModal, setShowAmenitiesModal] = useState<AmenitiesFirestoreResponse | null>(null);
  const hotelDocument = useGetHotelDocumentFromFirestore(id || '');
  const { data: hotelData } = hotelDocument;

  const hotelAmenitiesDocument = useGetHotelAmenitiesFromFirestore(id);
  const sortedAmenities = sortByOrder(hotelAmenitiesDocument.data);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [dominantColor, setDominantColor] = useState<string>('#fff');

  useEffect(() => {
    const img = imgRef.current;
    if (img && hotelData?.brand_url) {
      const fac = new FastAverageColor();

      fac.getColorAsync(img, {
        left: 0,
        top: 0,
        width: 10,
        height: 10,
      })
        .then((color) => {
          const [r, g, b, a] = color.value;

          if (a === 0 || (r === 0 && g === 0 && b === 0)) {
            setDominantColor('#ffffff');
          } else {
            setDominantColor(color.rgb);
          }
        })
        .catch((error: Error) => {
          console.error('Color extraction failed:', error);
        });
    }
  }, [hotelData?.brand_url]);

  const { isLoading } = useQueryHelper({
    queries: [
      hotelDocument],
  });

  if (isLoading) {
    return <Spinner global />;
  }

  return (
    <>
      <Helmet>
        <title>Hotelspot | Informajce hotelowe</title>
        <meta name="description" content="Hotelspot - informacje hotelowe" />
      </Helmet>
      <div className="about_hotel">
        <Modal
          size="2xl"
          title=""
          isOpen={showModal !== null}
          onClose={() => setShowModal(null)}
        >
          <Fragment key="modal">
            <Image src={showModal || ''} />
          </Fragment>
        </Modal>
        <Modal
          size="2xl"
          isOpen={showAmenitiesModal !== null}
          onClose={() => setShowAmenitiesModal(null)}
          title={showAmenitiesModal?.title[currentLang]}
        >
          <Fragment key="modal">
            <ContentContainer>
              <Logo src={showAmenitiesModal?.imageUrl} alt="prac" />
              <TextContainer>
                {showAmenitiesModal?.description[currentLang]}
              </TextContainer>
            </ContentContainer>
          </Fragment>
        </Modal>
        {hotelData
        && (
        <>
          <Section>
            <CoverImageWrapper>
              <Banner
                image={hotelData.cover_photo || ''}
                height="300px"
              />
              <HotelLogoWrapper>
                <HotelLogoContainer style={{ backgroundColor: dominantColor }}>
                  <img
                    ref={imgRef}
                    src={hotelData.brand_url}
                    alt={hotelData.name}
                    style={{ width: '100px', height: 'auto', display: 'block' }}
                    crossOrigin="anonymous"
                  />
                </HotelLogoContainer>
                <Flex itemAlign="center" justifyContent="center" direction="column" gap="3px">
                  <HotelName>{hotelData.name}</HotelName>
                  <Addres>{hotelData.address.street}
                    {' '}{hotelData.address.number} {hotelData.address.postal_code} {hotelData.address.city_name}
                  </Addres>
                  <Flex direction="row" itemAlign="center" justifyContent="center" gap="3px" margin="10px 0">
                    {Array.from({ length: hotelData.stars || 0 }).map((_, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Image key={index} src={STAR} style={{ width: '20px', height: '20px' }} alt="star" />
                    ))}
                  </Flex>
                </Flex>

              </HotelLogoWrapper>
            </CoverImageWrapper>
            {hotelData?.short_description?.[currentLang] && (
            <Text>
              {hotelData?.short_description?.[currentLang] || ''}
            </Text>
            )}
          </Section>
          <Section>
            {!!hotelData?.gallery && (
              <LightboxGallery urls={hotelData?.gallery || []} />
            )}
          </Section>

          {sortedAmenities && sortedAmenities.length > 0 && (
          <Section>
            <SectionTitle>{t('hotel_info_screen.amenities')}</SectionTitle>
            <Swiper
              spaceBetween={5}
              style={{ width: '100%' }}
              slidesPerView={2}
              loop
              centeredSlides
              breakpoints={{
                768: {
                  slidesPerView: 3.2,
                  centeredSlides: true,
                },
                1024: {
                  slidesPerView: 4.4,
                  centeredSlides: true,
                },
              }}
            >
              {sortedAmenities?.map((amenities: AmenitiesFirestoreResponse) => (
                <SwiperSlide
                  key={amenities.id}
                >
                  <Card
                    action={() => setShowAmenitiesModal(amenities)}
                    isSmall
                    key={amenities?.id}
                    image={amenities?.imageUrl}
                    title={amenities?.title[currentLang]}
                    isMicro
                  />
                </SwiperSlide>
              ))}
            </Swiper>

          </Section>
          )}

          {(hotelData?.director?.photo_url
              && hotelData?.director?.name
              && hotelData?.director?.text.desc[currentLang]
              && hotelData?.name && hotelData?.director?.quote[currentLang]) && (
              <LastSection>
                <CardDirector
                  cover={hotelData?.director?.photo_url || IMAGE_PLACEHOLDER}
                  title={hotelData?.director?.name || ''}
                  description={hotelData?.director?.quote[currentLang] || ''}
                />
                <Quote
                  title={hotelData?.name || ''}
                  quote={hotelData?.director?.text.desc[currentLang] || ''}
                />
              </LastSection>
          )}
        </>
        )}
      </div>
    </>
  );
};
